import { pull } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

// Geolocation configuration constants
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const GPS_TIMEOUT_MS = 20_000;
const GPS_MAX_AGE_MS = 5_000;
const RECHECK_TIMEOUT_MS = 1500;

export type GeoLocation = {
    pending: boolean;
    error: GeolocationPositionError | null;
    coords: Pick<GeolocationCoordinates, 'latitude' | 'longitude'> | null;
};

/**
 * Detect Safari browser (doesn't support Permissions API for geolocation)
 */
const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

/**
 * This is slightly suspect but hear me out: some browsers have a bug where geolocation.getCurrentPosition
 * never returns if anyone has an active geolocation.watchPosition, and arguably we don't actually need more than
 * one state tracking location app-wide. This provides an easy way to share the current view of location with a single
 * watcher across multiple useGeoLocation() invocations. An alternative might be to stick this state in a context
 * somewhere, but that feels a little heavyweight for our problem, and also makes it somewhat more complicated to
 * mix consumers that truly demand geolocation (e.g. current location dot) from consumers that are merely happy to
 * take advantage of location if it has already been granted (e.g. offering sort-by-proximity).
 */
const staticState = {
    location: null as GeoLocation | null,
    watchId: null as number | null,
    watchers: [] as ((l: GeoLocation) => void)[],
    permissionState: null as PermissionState | null,
    permissionWatchers: [] as ((p: PermissionState) => void)[],
    hasInteracted: false, // Track if user has triggered a location request
    pendingRequest: null as Promise<GeoLocation> | null, // Prevents concurrent requests
};

const unsubscribeWatcher = (setLocation: (l: GeoLocation) => void) => {
    pull(staticState.watchers, setLocation);
    if (!staticState.watchers.length) {
        // if we were the last one, stop watching
        if (staticState.watchId) navigator.geolocation.clearWatch(staticState.watchId);
        staticState.location = null;
        staticState.watchId = null;
    }
};

const updateLocation = (location: GeoLocation) => {
    staticState.location = location;
    staticState.watchers.forEach((setLocation) => setLocation(location));
};

const updatePermission = (state: PermissionState) => {
    staticState.permissionState = state;
    staticState.permissionWatchers.forEach((setPermission) => setPermission(state));
};

const unsubscribePermissionWatcher = (setPermission: (p: PermissionState) => void) => {
    pull(staticState.permissionWatchers, setPermission);
};

/**
 * A React hook for managing browser geolocation with permission state tracking.
 *
 * @param options - Configuration options
 * @param options.dontRequestPermission - If true (default), won't automatically request permission on mount
 *
 * @returns An object containing:
 * - `coords` - The current coordinates (latitude, longitude) or null if not available
 * - `permission` - The current permission state: 'granted' | 'denied' | 'prompt' | null
 * - `requestLocation` - A function to request the user's location (triggers browser permission prompt if needed)
 *
 * @example
 * ```tsx
 * const { coords, permission, requestLocation } = useGeoLocation();
 *
 * // Hide if definitely denied (Chrome only reliably knows this)
 * if (permission === 'denied') return null;
 *
 * // If coords already available (prior grant), use them
 * if (coords) {
 *     // Use coords directly
 * }
 *
 * // Otherwise, trigger an action that calls requestLocation() which will prompt the user for permission
 * async function onUserWantsLocation() {
 *     try {
 *         await requestLocation();
 *         // Now coords will be available via the hook's state
 *     } catch (e) {
 *         // Handle denial or error
 *     }
 * }
 * ```
 */
export const useGeoLocation = ({ dontRequestPermission = true }: { dontRequestPermission?: boolean } = {}) => {
    const [permission, setPermission] = useState<PermissionState | null>(staticState.permissionState);
    const [location, setLocation] = useState<GeoLocation>({
        pending: true,
        coords: null,
        error: null,
    });

    // Imperative function to request location (triggers prompt on Safari)
    const requestLocation = useCallback(() => {
        // If a request is already in progress, return the existing promise
        if (staticState.pendingRequest) {
            return staticState.pendingRequest;
        }

        const promise = new Promise<GeoLocation>((resolve, reject) => {
            if (!('geolocation' in navigator)) {
                const error = { code: 0, message: 'Geolocation not supported' } as GeolocationPositionError;
                staticState.pendingRequest = null;
                reject(error);
                return;
            }

            let retryCount = 0;

            const attemptGetPosition = () => {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        staticState.hasInteracted = true;
                        staticState.pendingRequest = null;
                        updatePermission('granted');
                        const newLocation: GeoLocation = {
                            pending: false,
                            coords: pos.coords,
                            error: null,
                        };
                        updateLocation(newLocation);

                        // Start watching if not already
                        if (!staticState.watchId) {
                            staticState.watchId = navigator.geolocation.watchPosition(
                                (currentPosition) => {
                                    updateLocation({
                                        pending: false,
                                        coords: currentPosition.coords,
                                        error: null,
                                    });
                                },
                                (err) => {
                                    if (err.code === 1) {
                                        updatePermission('denied');
                                    }
                                }
                            );
                        }
                        resolve(newLocation);
                    },
                    (err) => {
                        staticState.hasInteracted = true;

                        // POSITION_UNAVAILABLE (code 2) or TIMEOUT (code 3) - Safari GPS "cold start" issue
                        // These errors mean permission WAS granted (otherwise we'd get code 1)
                        if (err.code === 2 || err.code === 3) {
                            updatePermission('granted'); // User granted, GPS just failed
                            if (retryCount < MAX_RETRIES) {
                                retryCount++;
                                setTimeout(attemptGetPosition, RETRY_DELAY_MS);
                                return;
                            }
                        }

                        if (err.code === 1) {
                            // PERMISSION_DENIED
                            updatePermission('denied');
                            updateLocation({ pending: false, coords: null, error: err });
                        }
                        // For transient errors after exhausting retries, don't clear coords
                        staticState.pendingRequest = null;
                        reject(err);
                    },
                    { enableHighAccuracy: false, timeout: GPS_TIMEOUT_MS, maximumAge: GPS_MAX_AGE_MS }
                );
            };

            attemptGetPosition();
        });

        staticState.pendingRequest = promise;
        return promise;
    }, []);

    useEffect(() => {
        const updateState = (state: PermissionState) => setPermission(state);
        staticState.permissionWatchers.push(updateState);

        if (staticState.permissionState) {
            setPermission(staticState.permissionState);
            return () => unsubscribePermissionWatcher(updateState);
        }

        if (!('permissions' in navigator)) {
            if (isSafari()) {
                setPermission('prompt');
                staticState.permissionState = 'prompt';
            } else {
                setPermission('denied');
                staticState.permissionState = 'denied';
            }
            return () => unsubscribePermissionWatcher(updateState);
        }

        let cleanup: (() => void) | undefined;

        (async () => {
            try {
                const status = await navigator.permissions.query({ name: 'geolocation' });

                const onPermissionChange = () => {
                    setPermission(status.state);
                    staticState.permissionState = status.state;
                };

                onPermissionChange();
                status.onchange = onPermissionChange;

                cleanup = () => {
                    status.onchange = null;
                };
            } catch {
                setPermission('prompt');
                staticState.permissionState = 'prompt';
            }
        })();

        return () => {
            unsubscribePermissionWatcher(updateState);
            cleanup?.();
        };
    }, []);

    useEffect(() => {
        // add ourself to the global list of watchers
        staticState.watchers.push(setLocation);

        // If someone's already watching, just register and wait for updates
        if (staticState.watchId) {
            if (staticState.location) {
                setLocation(staticState.location);
            }
            return () => unsubscribeWatcher(setLocation);
        }

        const onCurrentPosition = (currentPosition: GeolocationPosition) => {
            updateLocation({
                pending: false,
                coords: currentPosition.coords,
                error: null,
            });
        };

        const onError = (error: GeolocationPositionError) => {
            // Only clear coords on PERMISSION_DENIED (code 1)
            // For POSITION_UNAVAILABLE (code 2) or TIMEOUT (code 3), keep last known good coords
            if (error.code === 1) {
                updateLocation({
                    pending: false,
                    coords: null,
                    error,
                });
                updatePermission('denied');
            }
            // Transient errors are ignored - keep last known coords
        };

        if (!('geolocation' in navigator)) {
            onError({
                code: 0,
                message: 'Geolocation not supported',
            } as GeolocationPositionError);
            return () => unsubscribeWatcher(setLocation);
        }

        // Don't auto-request if explicitly told not to, or if permission not yet granted
        if (dontRequestPermission && permission !== 'granted') {
            setLocation({
                pending: false,
                coords: null,
                error: { code: 0, message: 'Geolocation permission not already granted' } as GeolocationPositionError,
            });
            return () => unsubscribeWatcher(setLocation);
        }

        // On Safari (permission === 'prompt'), don't auto-request - wait for explicit requestLocation() call
        if (permission === 'prompt') {
            setLocation({
                pending: false,
                coords: null,
                error: null,
            });
            return () => unsubscribeWatcher(setLocation);
        }

        // Permission is 'granted' - proceed with watching (but only if not already watching)
        if (permission === 'granted' && !staticState.watchId) {
            navigator.geolocation.getCurrentPosition(onCurrentPosition, onError);
            staticState.watchId = navigator.geolocation.watchPosition(onCurrentPosition, onError);
        }

        return () => unsubscribeWatcher(setLocation);
    }, [dontRequestPermission, permission]);

    /*
        Tab-focus recheck: detect if user changed Safari site permissions while away
        Safari's permission prompt dialog is a change in visibility rather than a Permissions API event.
     */
    useEffect(() => {
        if (!isSafari()) {
            // Chrome uses Permissions API change listener
            return;
        }

        const recheck = async () => {
            if (!staticState.hasInteracted) {
                // Only recheck after user has previously interacted with geolocation and not on initial page load
                return;
            }

            // Don't interfere with an active request
            if (staticState.pendingRequest) {
                return;
            }

            try {
                const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        timeout: RECHECK_TIMEOUT_MS,
                        maximumAge: 0,
                    });
                });
                // Permission was (re-)granted
                updatePermission('granted');
                updateLocation({
                    pending: false,
                    coords: pos.coords,
                    error: null,
                });
            } catch (err) {
                if (err instanceof GeolocationPositionError && err.code === 1) {
                    // Permission was revoked
                    updatePermission('denied');
                    updateLocation({ pending: false, coords: null, error: err });
                }
            }
        };

        const onFocus = () => recheck();
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') recheck();
        };

        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    return { ...location, permission, requestLocation };
};
